import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, MicOff, Volume2, VolumeX, Settings, X } from 'lucide-react';

interface VoiceNavigationProps {
  isEnabled: boolean;
  onToggle: () => void;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  addEventListener(type: 'result', listener: (event: SpeechRecognitionEvent) => void): void;
  addEventListener(type: 'error', listener: (event: SpeechRecognitionErrorEvent) => void): void;
  addEventListener(type: 'end', listener: () => void): void;
  addEventListener(type: 'start', listener: () => void): void;
}

interface SpeechRecognitionStatic {
  new(): SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionStatic;
    webkitSpeechRecognition: SpeechRecognitionStatic;
  }
}

const VoiceNavigation: React.FC<VoiceNavigationProps> = ({ isEnabled, onToggle }) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [speechRate, setSpeechRate] = useState(1);
  const [speechVolume, setSpeechVolume] = useState(0.8);
  
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const navigate = useNavigate();

  // Voice commands mapping
  const voiceCommands = {
    navigation: {
      'go home': '/',
      'home page': '/',
      'go to home': '/',
      'about us': '/about',
      'about page': '/about',
      'services': '/services',
      'specialty services': '/services',
      'contact us': '/contact',
      'contact page': '/contact',
      'location': '/location',
      'clinic location': '/location',
      'provider': '/provider',
      'provider page': '/provider',
      'resource center': '/resource-center',
      'resources': '/resource-center',
      'direct access': '/direct-access',
    },
    actions: {
      'schedule appointment': () => {
        window.open(
          "https://holisticmanualptllc.patient.sprypt.com/create-appointments?clinic=460&therapist=392849&type=2683",
          "_blank"
        );
        speak("Opening appointment scheduling page");
      },
      'book appointment': () => {
        window.open(
          "https://holisticmanualptllc.patient.sprypt.com/create-appointments?clinic=460&therapist=392849&type=2683",
          "_blank"
        );
        speak("Opening appointment scheduling page");
      },
      'call clinic': () => {
        window.location.href = "tel:+15179080800";
        speak("Calling clinic now");
      },
      'phone number': () => {
        speak("The clinic phone number is 5 1 7, 9 0 8, 0 8 0 0");
      },
      'clinic hours': () => {
        speak("We are open Monday through Friday from 7 AM to 7 PM. We are closed on Saturday and Sunday.");
      },
      'address': () => {
        speak("Our address is 5700 Executive Drive, Suite 3, Lansing, Michigan, 48911");
      },
    },
    help: {
      'help': () => {
        speak("Voice navigation is active. You can say commands like: go home, about us, services, contact us, schedule appointment, call clinic, or ask for clinic hours and address. Say 'stop listening' to turn off voice navigation.");
      },
      'voice commands': () => {
        speak("Available commands include: Navigation - go home, about us, services, contact us, location, provider, resource center. Actions - schedule appointment, call clinic, phone number, clinic hours, address. Say help for this list again.");
      },
    },
    control: {
      'stop listening': () => {
        setIsListening(false);
        onToggle();
        speak("Voice navigation stopped");
      },
      'stop voice': () => {
        setIsListening(false);
        onToggle();
        speak("Voice navigation stopped");
      },
    }
  };

  // Text-to-speech function
  const speak = (text: string) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = speechRate;
      utterance.volume = speechVolume;
      utterance.lang = 'en-US';
      
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      
      window.speechSynthesis.speak(utterance);
    }
  };

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setIsSupported(true);
      recognitionRef.current = new SpeechRecognition();
      
      const recognition = recognitionRef.current;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.addEventListener('result', (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          }
        }
        
        if (finalTranscript) {
          setTranscript(finalTranscript);
          processVoiceCommand(finalTranscript.toLowerCase().trim());
        }
      });

      recognition.addEventListener('error', (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        if (event.error === 'not-allowed') {
          speak("Microphone access is required for voice navigation. Please allow microphone access and try again.");
        }
      });

      recognition.addEventListener('end', () => {
        if (isEnabled && isListening) {
          // Restart recognition if it should still be active
          setTimeout(() => {
            try {
              recognition.start();
            } catch (error) {
              console.error('Error restarting recognition:', error);
              setIsListening(false);
            }
          }, 100);
        }
      });
    } else {
      setIsSupported(false);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [isEnabled, isListening]);

  // Process voice commands
  const processVoiceCommand = (command: string) => {
    
    // Check navigation commands
    for (const [phrase, path] of Object.entries(voiceCommands.navigation)) {
      if (command.includes(phrase)) {
        navigate(path);
        speak(`Navigating to ${phrase}`);
        return;
      }
    }
    
    // Check action commands
    for (const [phrase, action] of Object.entries(voiceCommands.actions)) {
      if (command.includes(phrase)) {
        action();
        return;
      }
    }
    
    // Check help commands
    for (const [phrase, action] of Object.entries(voiceCommands.help)) {
      if (command.includes(phrase)) {
        action();
        return;
      }
    }
    
    // Check control commands
    for (const [phrase, action] of Object.entries(voiceCommands.control)) {
      if (command.includes(phrase)) {
        action();
        return;
      }
    }
    
    // If no command matched
    speak("I didn't understand that command. Say 'help' to hear available commands.");
  };

  // Start/stop listening
  const toggleListening = () => {
    if (!isSupported) {
      alert('Voice recognition is not supported in your browser. Please use Chrome, Safari, or Edge for voice navigation.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      speak("Voice navigation paused");
    } else {
      try {
        recognitionRef.current?.start();
        setIsListening(true);
        speak("Voice navigation activated. Say 'help' for available commands.");
      } catch (error) {
        console.error('Error starting recognition:', error);
        speak("Unable to start voice recognition. Please check your microphone permissions.");
      }
    }
  };

  // Stop speech
  const stopSpeech = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  if (!isEnabled) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40">
      {/* Settings Panel */}
      {showSettings && (
        <div className="absolute bottom-20 -right-2 sm:right-0 bg-white rounded-lg shadow-xl border border-slate-200 p-6 w-80 max-w-[calc(100vw-2rem)] sm:max-w-none mb-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Voice Settings</h3>
            <button
              onClick={() => setShowSettings(false)}
              className="p-1 hover:bg-slate-100 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Speech Rate: {speechRate}x
              </label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={speechRate}
                onChange={(e) => setSpeechRate(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Speech Volume: {Math.round(speechVolume * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={speechVolume}
                onChange={(e) => setSpeechVolume(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
            
            <button
              onClick={() => speak("This is a test of the speech settings")}
              className="w-full bg-slate-900 text-white py-2 px-4 rounded-lg hover:bg-slate-800 transition-colors"
            >
              Test Speech
            </button>
          </div>
        </div>
      )}

      {/* Transcript Display */}
      {transcript && isListening && (
        <div className="absolute bottom-20 -right-2 sm:right-0 bg-white rounded-lg shadow-xl border border-slate-200 p-4 w-80 max-w-[calc(100vw-2rem)] sm:max-w-none mb-2">
          <div className="text-sm text-slate-600 mb-2">Listening...</div>
          <div className="text-slate-900 font-medium">{transcript}</div>
        </div>
      )}

      {/* Voice Control Panel */}
      <div className="bg-white rounded-full shadow-xl border border-slate-200 p-2 flex items-center space-x-1 sm:space-x-2">
        {/* Settings Button */}
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="p-2 sm:p-3 hover:bg-slate-100 rounded-full transition-colors flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12"
          title="Voice Settings"
        >
          <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
        </button>

        {/* Stop Speech Button */}
        <button
          onClick={stopSpeech}
          disabled={!isSpeaking}
          className={`p-2 sm:p-3 rounded-full transition-colors flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 ${
            isSpeaking 
              ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' 
              : 'text-slate-400 cursor-not-allowed'
          }`}
          title="Stop Speech"
        >
          {isSpeaking ? <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" /> : <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />}
        </button>

        {/* Main Voice Toggle */}
        <button
          onClick={toggleListening}
          className={`p-3 sm:p-4 rounded-full transition-all duration-300 flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 ${
            isListening
              ? 'bg-red-500 text-white hover:bg-red-600 shadow-lg'
              : 'bg-slate-900 text-white hover:bg-slate-800 shadow-lg'
          }`}
          title={isListening ? 'Stop Voice Navigation' : 'Start Voice Navigation'}
        >
          {isListening ? (
            <MicOff className="w-5 h-5 sm:w-6 sm:h-6" />
          ) : (
            <Mic className="w-5 h-5 sm:w-6 sm:h-6" />
          )}
        </button>

        {/* Close Voice Navigation */}
        <button
          onClick={onToggle}
          className="p-2 sm:p-3 hover:bg-slate-100 rounded-full transition-colors flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12"
          title="Close Voice Navigation"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
        </button>
      </div>

      {/* Status Indicator */}
      {isListening && (
        <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
      )}
    </div>
  );
};

export default VoiceNavigation;